import type { CareManagementQueryResponse } from "../types/care-management";
import type { WorkingDrgSummary } from "../components/CAiRE/PredictiveDisposition";

const abbreviateDrgType = (drgType?: string | null) => {
  const normalized = drgType?.trim();
  if (!normalized) return undefined;

  const lower = normalized.toLowerCase();
  if (lower.startsWith("medical")) return "MED";
  if (lower.startsWith("surgical")) return "SURG";

  return normalized.slice(0, 4).toUpperCase();
};

const parseWeight = (value?: string | number | null) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number.parseFloat(value.replace(/,/g, "").trim());
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
};

/**
 * Maps care-management API fields into the Predicted DC Disposition
 * Working DRG / DRG Weight / Primary Diagnosis strip.
 */
export const buildWorkingDrgSummary = (
  payload?: CareManagementQueryResponse | null
): WorkingDrgSummary | null => {
  if (!payload) return null;

  const coding = payload.review?.clinicalCodingSummary;
  const msdrg = payload.case?.msdrg;
  const principal = coding?.workingPrincipalDiagnosis?.[0];

  const workingDrgCode =
    coding?.drg?.msDrg?.trim() || msdrg?.value?.trim() || "";
  const workingDrgDescription =
    coding?.drg?.description?.trim() || msdrg?.description?.trim() || "";
  const workingDrgCategory = abbreviateDrgType(
    coding?.drg?.drgType || msdrg?.details?.TYPE
  );
  const drgWeight =
    parseWeight(coding?.drg?.relativeWeight) ??
    parseWeight(msdrg?.details?.Weights);

  const primaryDiagnosisCode = principal?.icd10cm?.trim() || "";
  const primaryDiagnosisDescription = principal?.description?.trim() || "";

  if (
    !workingDrgCode &&
    !workingDrgDescription &&
    drgWeight === undefined &&
    !primaryDiagnosisCode &&
    !primaryDiagnosisDescription
  ) {
    return null;
  }

  return {
    workingDrgCode: workingDrgCode || "--",
    workingDrgDescription: workingDrgDescription || "--",
    workingDrgCategory,
    drgWeight: drgWeight ?? Number.NaN,
    primaryDiagnosisCode: primaryDiagnosisCode || "--",
    primaryDiagnosisDescription: primaryDiagnosisDescription || "--",
  };
};
